import { Suspense } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { SettingsModalProvider } from "@/contexts/SettingsModalContext";
import LandingPage from "./pages/LandingPage";
import PublicLayout from "./components/PublicLayout";
import ScrollToTop from "./components/ScrollToTop";
import { usePostHogPageviews } from "./hooks/usePostHogPageviews";

const SettingsModal = lazyWithRetry(() => import("@/components/SettingsModal"));
const CookieBanner = lazyWithRetry(() => import("./components/CookieBanner"));

const PricingPage = lazyWithRetry(() => import("./pages/PricingPage"));
const ResourcesPage = lazyWithRetry(() => import("./pages/ResourcesPage"));
const SupportPage = lazyWithRetry(() => import("./pages/SupportPage"));
const ReviewsPage = lazyWithRetry(() => import("./pages/ReviewsPage"));
const AuthPage = lazyWithRetry(() => import("./pages/AuthPage"));
const EmailVerificationPage = lazyWithRetry(() => import("./pages/EmailVerificationPage"));
const ResetPasswordPage = lazyWithRetry(() => import("./pages/ResetPasswordPage"));
const OnboardingPage = lazyWithRetry(() => import("./pages/OnboardingPage"));
const DashboardPage = lazyWithRetry(() => import("./pages/DashboardPage"));
const ExamPage = lazyWithRetry(() => import("./pages/ExamPage"));
const ExamSessionPage = lazyWithRetry(() => import("./pages/ExamSessionPage"));
const PracticeSessionPage = lazyWithRetry(() => import("./pages/PracticeSessionPage"));
const ResultsDetailPage = lazyWithRetry(() => import("./pages/ResultsDetailPage"));
const AnalyticsPage = lazyWithRetry(() => import("./pages/AnalyticsPage"));
const SettingsPage = lazyWithRetry(() => import("./pages/SettingsPage"));
const TheoryGuidePage = lazyWithRetry(() => import("./pages/TheoryGuidePage"));
const TheorySectionPage = lazyWithRetry(() => import("./pages/TheorySectionPage"));
const PaymentSuccessPage = lazyWithRetry(() => import("./pages/PaymentSuccessPage"));
const PredictorPage = lazyWithRetry(() => import("./pages/PredictorPage"));
const PredictorResultsPage = lazyWithRetry(() => import("./pages/PredictorResultsPage"));
const PredictorCourseDetailPage = lazyWithRetry(() => import("./pages/PredictorCourseDetailPage"));
const PredictorComparePage = lazyWithRetry(() => import("./pages/PredictorComparePage"));
const PredictorUpgradePage = lazyWithRetry(() => import("./pages/PredictorUpgradePage"));
const AdminPage = lazyWithRetry(() => import("./pages/AdminPage"));
const PrivacyPage = lazyWithRetry(() => import("./pages/PrivacyPage"));
const TermsPage = lazyWithRetry(() => import("./pages/TermsPage"));
const CookiePage = lazyWithRetry(() => import("./pages/CookiePage"));
const TrustPage = lazyWithRetry(() => import("./pages/TrustPage"));
const BlogTilIExamGuidePage = lazyWithRetry(() => import("./pages/BlogTilIExamGuidePage"));
const BlogTilIFourWeekStudyPlan = lazyWithRetry(() => import("./pages/BlogTilIFourWeekStudyPlan"));
const NotFoundPage = lazyWithRetry(() => import("./pages/NotFoundPage"));
const ProtectedRoute = lazyWithRetry(() => import("./components/ProtectedRoute"));
const AdminRoute = lazyWithRetry(() => import("./components/AdminRoute"));

const queryClient = new QueryClient();

const RouterAnalytics = () => {
  usePostHogPageviews();
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <Toaster />
      <BrowserRouter>
        <SettingsModalProvider>
        <ScrollToTop />
        <RouterAnalytics />
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-background"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" aria-label="Loading" /></div>}>
        <SettingsModal />
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<LandingPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/reviews" element={<ReviewsPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/cookies" element={<CookiePage />} />
            <Route path="/trust" element={<TrustPage />} />
            <Route path="/blog/til-i-exam-guide" element={<BlogTilIExamGuidePage />} />
            <Route path="/blog/til-i-4-week-study-plan" element={<BlogTilIFourWeekStudyPlan />} />
          </Route>
          <Route path="/login" element={<AuthPage initialTab="signin" />} />
          <Route path="/register" element={<AuthPage initialTab="signup" />} />
          <Route path="/verify-email" element={<EmailVerificationPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/exam" element={<ProtectedRoute><ExamPage /></ProtectedRoute>} />
          <Route path="/exam/practice/:section" element={<ProtectedRoute><PracticeSessionPage /></ProtectedRoute>} />
          <Route path="/exam/:attemptId" element={<ProtectedRoute><ExamSessionPage /></ProtectedRoute>} />
          <Route path="/results/:attemptId" element={<ProtectedRoute><ResultsDetailPage /></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/theory" element={<ProtectedRoute><TheoryGuidePage /></ProtectedRoute>} />
          <Route path="/theory/:section" element={<ProtectedRoute><TheorySectionPage /></ProtectedRoute>} />
          <Route path="/predictor" element={<ProtectedRoute><PredictorPage /></ProtectedRoute>} />
          <Route path="/predictor/results" element={<ProtectedRoute><PredictorResultsPage /></ProtectedRoute>} />
          <Route path="/predictor/upgrade" element={<ProtectedRoute><PredictorUpgradePage /></ProtectedRoute>} />
          <Route path="/predictor/course/:courseCode" element={<ProtectedRoute><PredictorCourseDetailPage /></ProtectedRoute>} />
          <Route path="/predictor/compare" element={<ProtectedRoute><PredictorComparePage /></ProtectedRoute>} />
          <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
          <Route path="/payment-success" element={<PaymentSuccessPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <CookieBanner />
        </Suspense>
        </SettingsModalProvider>
      </BrowserRouter>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
